import { AxiosError } from 'axios';
import { enqueueSnackbar } from 'notistack';
import { queryClient } from './queryKeys';

let isRedirecting = false;

/**
 * Handles session expiration and authentication errors
 * Clears all auth data and redirects to login
 */
const handleAuthError = (error: AxiosError) => {
    // Prevent multiple redirects
    if (isRedirecting) {
        return Promise.reject(error);
    }

    const currentPath = window.location.pathname;
    const publicPaths = ['/login', '/signup', '/register', '/forgot-password', '/reset-password'];

    // Don't redirect if already on a public page
    if (publicPaths.some(path => currentPath.includes(path))) {
        return Promise.reject(error);
    }

    isRedirecting = true;

    // Clear all authentication data
    localStorage.removeItem('access_token');
    localStorage.removeItem('refresh_token');
    localStorage.removeItem('is_login');
    localStorage.removeItem('user');
    sessionStorage.removeItem('access_token');
    sessionStorage.removeItem('refresh_token');

    // Clear Recoil persist storage
    const recoilKeys = ['recoil-persist'];
    recoilKeys.forEach(key => {
        localStorage.removeItem(key);
        sessionStorage.removeItem(key);
    });

    // Clear React Query cache
    queryClient.clear();

    // Show notification to user
    enqueueSnackbar('Your session has expired. Please login again.', {
        variant: 'warning',
        autoHideDuration: 4000,
    });

    // Save the current URL for redirect after login
    const returnUrl = window.location.pathname + window.location.search;
    if (returnUrl !== '/login' && returnUrl !== '/') {
        localStorage.setItem('return_url', returnUrl);
    }

    // Redirect to login after a brief delay
    setTimeout(() => {
        window.location.href = `/login?redirect=${encodeURIComponent(returnUrl)}`;
        isRedirecting = false;
    }, 500);

    return Promise.reject(error);
};

/**
 * Setup Axios response interceptor to handle authentication errors
 * DISABLED: Using instance-level interceptors in setAuthHeader.ts instead
 * This prevents duplicate interceptors from firing
 */
export const setupAxiosInterceptor = () => {
    // Interceptors are now handled at the instance level in setAuthHeader.ts
    // This avoids conflicts with multiple interceptor chains
    console.log('✅ Session management initialized (using instance interceptors)');
};

/**
 * Manual logout function that can be called from anywhere in the app
 */
export const performLogout = () => {
    // Clear all authentication data
    localStorage.removeItem('access_token');
    localStorage.removeItem('refresh_token');
    localStorage.removeItem('is_login');
    localStorage.removeItem('user');
    localStorage.removeItem('return_url');
    sessionStorage.removeItem('access_token');
    sessionStorage.removeItem('refresh_token');

    // Clear Recoil persist storage
    const recoilKeys = ['recoil-persist'];
    recoilKeys.forEach(key => {
        localStorage.removeItem(key);
        sessionStorage.removeItem(key);
    });

    // Clear React Query cache
    queryClient.clear();

    enqueueSnackbar('Logged out successfully', {
        variant: 'info',
        autoHideDuration: 2000,
    });

    // Redirect to login
    window.location.href = '/login';
};
